import React from 'react';
import { motion } from 'framer-motion';
import { Search, Filter, FileSpreadsheet, CheckCircle2, TrendingUp, MessageSquare, DollarSign, Link as LinkIcon, Globe, Users, Mail, BadgeCheck, ChevronDown, Building2, Copy } from 'lucide-react';
// Platform Icons
const TTIco = () => <img src="https://cdn.magicpatterns.com/patterns/figma-images/HPPMfQF9zbBNm5oHh69dtz/362-1248.png" alt="TikTok" className="w-3.5 h-3.5 inline-block" data-id="element-1985" data-mp="2nfrx14ui931p" />;
const IGIco = () => <img src="https://cdn.magicpatterns.com/patterns/figma-images/HPPMfQF9zbBNm5oHh69dtz/362-1249.png" alt="Instagram" className="w-3.5 h-3.5 inline-block" data-id="element-1986" data-mp="1urvz5wt7953c" />;
const WindowBar = ({
  title,
  subtitle
}: {
  title: string;
  subtitle?: string;
}) => <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100 bg-gray-50/70" data-id="element-1987" data-mp="24npajcv6msjj">
    <div className="flex items-center gap-1.5" data-id="element-1988" data-mp="1gxuwwt31gzy8">
      <span className="w-2.5 h-2.5 rounded-full bg-red-400/70" data-id="element-1989" data-mp="1c1o13t4cx4u0" />
      <span className="w-2.5 h-2.5 rounded-full bg-amber-400/70" data-id="element-1990" data-mp="1zm8wltf5sj9b" />
      <span className="w-2.5 h-2.5 rounded-full bg-emerald-400/70" data-id="element-1991" data-mp="1zlr0qxwes71x" />
    </div>
    <span className="ml-2 text-xs font-semibold text-gray-600" data-id="element-1992" data-mp="23msvp2jalwy3">{title}</span>
    {subtitle && <span className="ml-auto text-[10px] text-gray-400" data-id="element-1993" data-mp="28flyhkv7av7j">{subtitle}</span>}
  </div>;
const floatY = (delay = 0, distance = 8) => ({
  animate: {
    y: [0, -distance, 0]
  },
  transition: {
    duration: 4,
    repeat: Infinity,
    ease: 'easeInOut' as const,
    delay
  }
});
// Shared generic creator data (photos + names + niches) reused across mockups.
const CREATORS = [{
  name: 'Elena Rodriguez',
  niche: 'Fashion',
  fol: '1.2M',
  views: '340K',
  eng: '4.2%',
  photo: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=80&h=80&q=80'
}, {
  name: 'Marcus Chen',
  niche: 'Tech',
  fol: '850K',
  views: '120K',
  eng: '5.1%',
  photo: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?auto=format&fit=crop&w=80&h=80&q=80'
}, {
  name: 'Sarah Jenkins',
  niche: 'Lifestyle',
  fol: '450K',
  views: '89K',
  eng: '3.8%',
  photo: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&w=80&h=80&q=80'
}, {
  name: 'David Kim',
  niche: 'Gaming',
  fol: '2.1M',
  views: '800K',
  eng: '6.4%',
  photo: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=80&h=80&q=80'
}, {
  name: 'Aisha Kohl',
  niche: 'Beauty',
  fol: '640K',
  views: '150K',
  eng: '4.9%',
  photo: 'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?auto=format&fit=crop&w=80&h=80&q=80'
}];
export const LayeredRosterMockup = () => {
  return <div className="relative h-[440px] w-full" data-id="element-1994" data-mp="1ewyasswj0buf">
      <div className="absolute -inset-6 bg-gradient-to-tr from-orange-100/40 via-zano-pink/20 to-zano-lavender/30 rounded-[2.5rem] blur-2xl -z-10" data-id="element-1995" data-mp="1evn6mrz6522s" />
      <div className="absolute inset-0 bg-white rounded-2xl border border-gray-200 shadow-float overflow-hidden flex flex-col text-left" data-id="element-1996" data-mp="3llqh9nb80k0u">
        <WindowBar title="My Roster" data-id="element-1997" data-mp="3qcyrumoaebfl" />

        {/* Toolbar */}
        <div className="p-3 border-b border-gray-100 flex items-center justify-between bg-white" data-id="element-1998" data-mp="3bnsul490evac">
          <div className="flex items-center gap-2" data-id="element-1999" data-mp="3glucvmym2qkr">
            <div className="relative w-48" data-id="element-2000" data-mp="3gmnb1zji87ra">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-2001" data-mp="862bf9crir0u" />
              <input type="text" placeholder="Search roster..." className="w-full pl-7 pr-2 py-1.5 border border-gray-200 rounded-md text-[11px] outline-none bg-gray-50" data-id="element-2002" data-mp="d4vox1s1ui10" />
            </div>
            <button className="flex items-center gap-1 border border-gray-200 bg-white text-gray-600 px-2.5 py-1.5 rounded-md text-[11px] font-medium" data-id="element-2003" data-mp="2jvld61lojeuw">
              <Filter size={12} data-id="element-2004" data-mp="2nz16anyjix2u" /> Filter
            </button>
          </div>
          <button className="bg-gray-900 text-white px-3 py-1.5 rounded-md text-[11px] font-bold" data-id="element-2005" data-mp="345mjtd0s7ji3">
            + Add Creator
          </button>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-hidden bg-gray-50/30" data-id="element-2006" data-mp="2ue9v2uaa59ga">
          <table className="w-full text-left text-[11px] whitespace-nowrap" data-id="element-2007" data-mp="2pfyjae0fcovf">
            <thead className="bg-gray-50 border-b border-gray-100 text-gray-500 uppercase" data-id="element-2008" data-mp="2pdvir01nbdcg">
              <tr data-id="element-2009" data-mp="3n2xhwuaog6vy">
                <th className="px-4 py-2.5 font-semibold" data-id="element-2010" data-mp="3n1cnd4od4ny3">Influencer</th>
                <th className="px-4 py-2.5 font-semibold" data-id="element-2011" data-mp="3i55jrijfvojb">Niche</th>
                <th className="px-4 py-2.5 font-semibold" data-id="element-2012" data-mp="3d4rfmbkykyqk">Followers</th>
                <th className="px-4 py-2.5 font-semibold" data-id="element-2013" data-mp="3947lzhzlgd96">Avg Views</th>
                <th className="px-4 py-2.5 font-semibold" data-id="element-2014" data-mp="3960gy00uh91c">Eng. Rate</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white" data-id="element-2015" data-mp="21o18mq8po2vw">
              {CREATORS.map((row, i) => <tr key={i} className="hover:bg-gray-50" data-id="element-2016" data-mp="d6qbptjli0e2" data-mpi={i}>
                  <td className="px-4 py-2.5 font-medium text-gray-900 flex items-center gap-2" data-id="element-2017" data-mp="d8b53fu6bdac">
                    <img src={row.photo} alt={row.name} className="w-5 h-5 rounded-full object-cover bg-gray-200" data-id="element-2018" data-mp="h8uyq9fjfyrq" />
                    {row.name}
                  </td>
                  <td className="px-4 py-2.5 text-gray-600" data-id="element-2019" data-mp="vxr6e5don9rt">{row.niche}</td>
                  <td className="px-4 py-2.5 text-gray-900 font-medium" data-id="element-2020" data-mp="vzkhpxz8qi65">
                    {row.fol}
                  </td>
                  <td className="px-4 py-2.5 text-gray-600" data-id="element-2021" data-mp="14xeax01pkvp3">{row.views}</td>
                  <td className="px-4 py-2.5 text-emerald-600 font-medium" data-id="element-2022" data-mp="19x0lf1q5jxu7">
                    {row.eng}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>

      {/* Floating Bulk Upload */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.5,
      delay: 0.3
    }} className="absolute -top-4 -right-4 sm:-right-6 z-20" data-id="element-2023" data-mp="3bovt769afi75">
        <motion.div {...floatY(0.2)} className="bg-white rounded-xl border border-gray-200 shadow-[0_20px_40px_-15px_rgba(0,0,0,0.15)] p-3 w-48" data-id="element-2024" data-mp="muyzf3ht20v0">
          <div className="flex items-center gap-2 mb-1.5" data-id="element-2025" data-mp="wto8q7vmyczn">
            <div className="p-1.5 bg-green-50 rounded-md" data-id="element-2026" data-mp="wt50xjzp94zc">
              <FileSpreadsheet size={14} className="text-green-600" data-id="element-2027" data-mp="1alkxc1y3i4ra" />
            </div>
            <p className="text-[11px] font-bold text-gray-900" data-id="element-2028" data-mp="16jpzx8jacbis">Bulk Upload</p>
          </div>
          <p className="text-[10px] text-gray-500 leading-snug" data-id="element-2029" data-mp="1kj8wr56nc0fs">
            Import your entire roster in seconds using Google Sheets.
          </p>
        </motion.div>
      </motion.div>

      {/* Floating Filters */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.5,
      delay: 0.5
    }} className="absolute -bottom-5 -left-4 sm:-left-6 z-20" data-id="element-2030" data-mp="1z5ei81edc9df">
        <motion.div {...floatY(0.9)} className="bg-white rounded-xl border border-gray-200 shadow-[0_20px_40px_-15px_rgba(0,0,0,0.15)] p-3 w-44" data-id="element-2031" data-mp="1u8o7ltlc25ze">
          <p className="text-[10px] font-bold text-gray-900 mb-2 flex items-center gap-1" data-id="element-2032" data-mp="1l7hmf77qo6zt">
            <Filter size={10} className="text-zano-pink" data-id="element-2033" data-mp="16itvxfcr2ngq" /> Active Filters
          </p>
          <div className="flex flex-wrap gap-1.5" data-id="element-2034" data-mp="1bh53fjfkolvs">
            <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-[9px] font-medium" data-id="element-2035" data-mp="xgkjgjxx8k0p">
              Followers &gt; 500K
            </span>
            <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-[9px] font-medium" data-id="element-2036" data-mp="11ho1nd3fys53">
              US-based
            </span>
            <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-[9px] font-medium" data-id="element-2037" data-mp="1cyvcngishks9">
              Tech & Gaming
            </span>
          </div>
        </motion.div>
      </motion.div>
    </div>;
};
export const LayeredSubmissionsMockup = () => {
  return <div className="relative h-[440px] w-full" data-id="element-2038" data-mp="132saianxtm5b">
      <div className="absolute -inset-6 bg-gradient-to-tr from-sky-100/40 via-zano-lavender/30 to-zano-pink/10 rounded-[2.5rem] blur-2xl -z-10" data-id="element-2039" data-mp="yzvyx3wteqav" />
      <div className="absolute inset-0 bg-white rounded-2xl border border-gray-200 shadow-float overflow-hidden flex flex-col text-left" data-id="element-2040" data-mp="u61a5m2crl4x">
        <WindowBar title="Submit to Casting Call" subtitle="Summer Skincare Launch" data-id="element-2041" data-mp="kag8jp174mvp" />

        <div className="p-4 space-y-4" data-id="element-2042" data-mp="f9sawkigp7si">
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-3" data-id="element-2043" data-mp="fcxwvqvsntk0">
            <h4 className="text-xs font-bold text-gray-900 mb-1" data-id="element-2044" data-mp="b9rvoxnjgske">
              Select from your roster
            </h4>
            <div className="flex items-center gap-2 mb-3" data-id="element-2045" data-mp="2myoed5w3z9b8">
              <div className="relative flex-1" data-id="element-2046" data-mp="2qx5gfb0o9tls">
                <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-2047" data-mp="2r01fj2o9aobt" />
                <input type="text" placeholder="Search..." className="w-full pl-7 pr-2 py-1.5 border border-gray-200 rounded-md text-[10px] outline-none bg-white" data-id="element-2048" data-mp="30v1vuf0n7app" />
              </div>
              <button className="flex items-center gap-1 border border-zano-pink/30 bg-zano-blush text-zano-pink px-2 py-1.5 rounded-md text-[10px] font-medium" data-id="element-2049" data-mp="2d724pdzf5ial">
                <Filter size={10} data-id="element-2050" data-mp="2hytl4vov27n0" /> Filter
              </button>
            </div>

            <div className="space-y-2" data-id="element-2051" data-mp="1khlf0o36ofi2">
              <div className="flex items-center justify-between bg-white border border-zano-pink/40 rounded-lg p-2 shadow-sm" data-id="element-2052" data-mp="1ph7t0zqu5zrx">
                <div className="flex items-center gap-2" data-id="element-2053" data-mp="pccwopiou1q4">
                  <img src="https://images.unsplash.com/photo-1524504388940-b1c1722653e1?auto=format&fit=crop&w=40&h=40" alt="" className="w-7 h-7 rounded-full object-cover" data-id="element-2054" data-mp="pdxrmg8wzmoi" />
                  <div data-id="element-2055" data-mp="147swlb5y8ruf">
                    <p className="text-[11px] font-bold text-gray-900" data-id="element-2056" data-mp="1482mkyqzuz04">
                      Elena Rodriguez
                    </p>
                    <p className="text-[9px] text-gray-500" data-id="element-2057" data-mp="bk6iva30c6tu">
                      1.2M Followers • 340K Avg Views
                    </p>
                  </div>
                </div>
                <CheckCircle2 size={16} className="text-zano-pink" data-id="element-2058" data-mp="ghf0n9k8u8uk" />
              </div>
            </div>
          </div>

          <div className="space-y-3" data-id="element-2059" data-mp="3k08abb9widjg">
            <div data-id="element-2060" data-mp="3jz6yh4l29n0o">
              <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-2061" data-mp="3jzq6ntkwsx1i">
                Your Quote
              </label>
              <div className="mt-1 relative" data-id="element-2062" data-mp="264grx1n77fb1">
                <DollarSign size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-2063" data-mp="2635nr0puc5je" />
                <input type="text" value="2,500" readOnly className="w-full pl-7 pr-3 py-2 border border-gray-200 rounded-md text-xs font-medium bg-gray-50" data-id="element-2064" data-mp="1x2h9q60g656i" />
              </div>
            </div>
            <div data-id="element-2065" data-mp="1iemeoiyyvoq9">
              <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-2066" data-mp="1ie47wseohs43">
                Notes for Brand
              </label>
              <textarea readOnly className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-md text-[11px] text-gray-700 bg-gray-50 h-16 resize-none" value="Elena has a highly engaged US female audience. She recently did a skincare campaign that performed 2x above average." data-id="element-2067" data-mp="1eaeo0g8y7ype" />
            </div>
            <button className="w-full bg-zano-pink text-white rounded-md py-2 text-xs font-bold shadow-sm" data-id="element-2068" data-mp="pr4lfym8mkht">
              Submit Influencer
            </button>
          </div>
        </div>
      </div>

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.5,
      delay: 0.4
    }} className="absolute -top-3 -left-4 sm:-left-6 z-20" data-id="element-2069" data-mp="gsj4g7wezdj5">
        <motion.div {...floatY(0.3, 6)} className="bg-white rounded-xl border border-gray-200 shadow-[0_15px_35px_-12px_rgba(0,0,0,0.15)] p-3 w-44" data-id="element-2070" data-mp="zqbuwapupuzn">
          <p className="text-[10px] font-bold text-gray-900 mb-1.5 flex items-center gap-1" data-id="element-2071" data-mp="13pazfu7o7pqk">
            <Filter size={10} className="text-zano-pink" data-id="element-2072" data-mp="1rff1hx57nhfg" /> Roster Filter
            Applied
          </p>
          <div className="space-y-1" data-id="element-2073" data-mp="1w9syg3zitwm8">
            <div className="flex justify-between text-[9px]" data-id="element-2074" data-mp="3gxls5yym3h1g">
              <span className="text-gray-500" data-id="element-2075" data-mp="3bzafzhkugug2">Followers</span>
              <span className="font-semibold text-gray-900" data-id="element-2076" data-mp="3byr7erh33iep">&gt; 50K</span>
            </div>
            <div className="flex justify-between text-[9px]" data-id="element-2077" data-mp="3qmvk9etggzpk">
              <span className="text-gray-500" data-id="element-2078" data-mp="3lok82xfoud46">Avg Views</span>
              <span className="font-semibold text-gray-900" data-id="element-2079" data-mp="3lr6pdosds6zl">&gt; 100K</span>
            </div>
          </div>
        </motion.div>
      </motion.div>

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.5,
      delay: 0.6
    }} className="absolute bottom-6 -right-4 sm:-right-6 z-20" data-id="element-2080" data-mp="2t17wtjez9rq6">
        <motion.div {...floatY(1.1)} className="bg-white rounded-xl border border-gray-200 shadow-[0_20px_40px_-15px_rgba(0,0,0,0.15)] p-3 w-48" data-id="element-2081" data-mp="n9atzokm4c6x">
          <div className="flex items-center gap-1.5 mb-1" data-id="element-2082" data-mp="2nyi5fae9r8bt">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" data-id="element-2083" data-mp="2suywaccus0sl" />
            <p className="text-[10px] font-bold text-gray-900" data-id="element-2084" data-mp="2wxlq2it33vsf">Custom Pitch</p>
          </div>
          <p className="text-[10px] text-gray-500 leading-snug" data-id="element-2085" data-mp="31wf6aizra0wa">
            Add your rate and notes directly to the submission to win the deal.
          </p>
        </motion.div>
      </motion.div>
    </div>;
};
export const LayeredMediaKitMockup = () => {
  return <div className="relative h-[440px] w-full" data-id="element-2086" data-mp="3bsrytdjke8pg">
      <div className="absolute -inset-6 bg-gradient-to-tr from-purple-100/40 via-zano-lavender/30 to-blue-100/20 rounded-[2.5rem] blur-2xl -z-10" data-id="element-2087" data-mp="3gkt6sn9p9d9o" />
      <div className="absolute inset-0 bg-white rounded-2xl border border-gray-200 shadow-float overflow-hidden flex flex-col text-left" data-id="element-2088" data-mp="3km4xyx8826jz">
        <WindowBar title="Public Media Kit" data-id="element-2089" data-mp="3ukuhsvjoi12v" />

        <div className="p-4 border-b border-gray-100 bg-gray-50/50 flex justify-between items-center" data-id="element-2090" data-mp="3ulm23a7tscdu">
          <div className="flex items-center gap-3" data-id="element-2091" data-mp="8l7ygh0wtz2p">
            <div className="w-10 h-10 bg-gray-900 text-white rounded-lg flex items-center justify-center text-xs font-bold shadow-sm" data-id="element-2092" data-mp="3k0r2j2oj1wk">
              ABC
            </div>
            <div data-id="element-2093" data-mp="3usocez0x6p44">
              <h3 className="text-sm font-bold text-gray-900" data-id="element-2094" data-mp="3qpjaoyonyrgj">
                ABC Talent Agency
              </h3>
              <p className="text-[11px] text-gray-500" data-id="element-2095" data-mp="rao3nhnjifpg">Exclusive Roster</p>
            </div>
          </div>
          <button className="flex items-center gap-1.5 bg-white border border-gray-200 text-gray-700 px-3 py-1.5 rounded-md text-[11px] font-bold shadow-sm" data-id="element-2096" data-mp="marwj9jjq85o">
            <LinkIcon size={12} data-id="element-2097" data-mp="hhysknvwj3un" /> Share Link
          </button>
        </div>

        <div className="p-4 flex-1 bg-white" data-id="element-2098" data-mp="dihlxr4qo0nw">
          <div className="flex items-center gap-2 mb-3" data-id="element-2099" data-mp="1a15rb7timl3y">
            <div className="relative flex-1" data-id="element-2100" data-mp="1a2ql2v80a00r">
              <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-2101" data-mp="159npkhjo4ghx" />
              <input type="text" placeholder="Brands can search your roster..." className="w-full pl-7 pr-2 py-1.5 border border-gray-200 rounded-md text-[10px] bg-gray-50" readOnly data-id="element-2102" data-mp="2zjujakofrp4q" />
            </div>
            <button className="flex items-center gap-1 border border-gray-200 bg-white text-gray-600 px-2 py-1.5 rounded-md text-[10px] font-medium" data-id="element-2103" data-mp="2upgjm62qqvu9">
              <Filter size={10} data-id="element-2104" data-mp="2lq38za2vs17y" /> Filters
            </button>
          </div>

          <div className="border border-gray-100 rounded-xl overflow-hidden" data-id="element-2105" data-mp="2lo08fw43qpoz">
            <table className="w-full text-left text-[11px] whitespace-nowrap" data-id="element-2106" data-mp="26ux0mggbbqbg">
              <thead className="bg-gray-50 border-b border-gray-100 text-gray-500 uppercase" data-id="element-2107" data-mp="2bsyiiy205jlc">
                <tr data-id="element-2108" data-mp="52sa99egp12u">
                  <th className="px-3 py-2 font-semibold" data-id="element-2109" data-mp="52ilfp55kzyq">Creator</th>
                  <th className="px-3 py-2 font-semibold" data-id="element-2110" data-mp="9zr55u5y9c23">Niche</th>
                  <th className="px-3 py-2 font-semibold" data-id="element-2111" data-mp="1uil7t7wqtyjx">
                    <TTIco data-id="element-2112" data-mp="1uj4ettkuv2j6" />
                  </th>
                  <th className="px-3 py-2 font-semibold text-right" data-id="element-2113" data-mp="1pk9w0rmw1dz2">Rates</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white" data-id="element-2114" data-mp="249pk1zv1b5b2">
                {CREATORS.slice(0, 4).map((c, i) => <tr key={i} className="hover:bg-gray-50" data-id="element-2115" data-mp="1zgmpboeitpta" data-mpi={i}>
                    <td className="px-3 py-2 font-medium text-gray-900 flex items-center gap-2" data-id="element-2116" data-mp="2d8ktdktdkvil">
                      <img src={c.photo} alt={c.name} className="w-6 h-6 rounded-full object-cover bg-gray-200" data-id="element-2117" data-mp="298ipd5063q05" />
                      {c.name}
                    </td>
                    <td className="px-3 py-2 text-gray-600" data-id="element-2118" data-mp="2i6mcu6upqwul">{c.niche}</td>
                    <td className="px-3 py-2 text-gray-900 font-medium" data-id="element-2119" data-mp="str7hm0ne153">
                      {c.fol}
                    </td>
                    <td className="px-3 py-2 text-right" data-id="element-2120" data-mp="218lfkmkixj0">
                      <span className="text-[10px] font-semibold text-emerald-600" data-id="element-2121" data-mp="20pet02dbvka">
                        View
                      </span>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.5,
      delay: 0.3
    }} className="absolute -top-4 -right-4 sm:-right-6 z-20" data-id="element-2122" data-mp="gp3fb4cbb7xo">
        <motion.div {...floatY(0.2)} className="bg-white rounded-xl border border-gray-200 shadow-[0_20px_40px_-15px_rgba(0,0,0,0.15)] p-3 w-48" data-id="element-2123" data-mp="3u4rn3nubzb0b">
          <div className="flex items-center gap-1.5 mb-1" data-id="element-2124" data-mp="14hts1fs1hv90">
            <Globe size={12} className="text-blue-500" data-id="element-2125" data-mp="19dr254cpm5dc" />
            <p className="text-[11px] font-bold text-gray-900" data-id="element-2126" data-mp="2twuvy90ab52g">
              Public & Shareable
            </p>
          </div>
          <p className="text-[10px] text-gray-500 leading-snug" data-id="element-2127" data-mp="2oxi1b2368tpb">
            Send your live media kit to brands. Built-in filters help you upsell
            more talent.
          </p>
        </motion.div>
      </motion.div>

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.5,
      delay: 0.5
    }} className="absolute -bottom-4 -left-4 sm:-left-6 z-20" data-id="element-2128" data-mp="2f7fan8jivru5">
        <motion.div {...floatY(0.8)} className="flex items-center gap-2 bg-gray-900 text-white rounded-full border border-gray-700 shadow-[0_15px_35px_-12px_rgba(0,0,0,0.3)] px-3 py-2" data-id="element-2129" data-mp="1w9m47w9owzs0">
          <CheckCircle2 size={14} className="text-emerald-400" data-id="element-2130" data-mp="3qo5925hpffd7" />
          <span className="text-[11px] font-medium" data-id="element-2131" data-mp="mbskvqcgnxci">
            Link copied to clipboard
          </span>
        </motion.div>
      </motion.div>
    </div>;
};
export const LayeredProfileOwnershipMockup = () => {
  return <div className="relative w-full text-left" data-id="element-2132" data-mp="cl7evhugjqjk">
      <div className="relative h-[600px] sm:h-[440px] md:h-[420px]" data-id="element-2133" data-mp="cjwbhj4xcksz">
        <div className="absolute inset-0 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-float" data-id="element-2134" data-mp="7m4nh07ehiu2">
          <WindowBar title="Creator Profile" subtitle="Live profile" data-id="element-2135" data-mp="3mm1e13uf1pn" />

          <div className="border-b border-gray-100 p-4" data-id="element-2136" data-mp="3uqken3geyxw0">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between" data-id="element-2137" data-mp="3psizuuq0tkqc">
              <div className="flex min-w-0 items-center gap-3" data-id="element-2138" data-mp="3ktft95ue76g9">
                <img src="https://cdn.magicpatterns.com/patterns/figma-images/HPPMfQF9zbBNm5oHh69dtz/3867-596.png" alt="Michelle Brown" className="h-14 w-14 shrink-0 rounded-full object-cover ring-2 ring-white shadow-sm" data-id="element-2139" data-mp="3kuzcn5bchxm9" />
                <div className="min-w-0" data-id="element-2140" data-mp="1rik4yzy6v887">
                  <div className="flex flex-wrap items-center gap-1.5" data-id="element-2141" data-mp="1vknhlrwlgiv2">
                    <h3 className="text-sm font-bold text-gray-900" data-id="element-2142" data-mp="1viki8h9jxddo">
                      Michelle Brown
                    </h3>
                    <BadgeCheck size={14} className="shrink-0 text-zano-pink" aria-label="Verified profile" data-id="element-2143" data-mp="25a73l6flssu5" />
                  </div>
                  <div className="mt-1 flex items-center gap-2 text-[10px] font-medium text-gray-600" data-id="element-2144" data-mp="1cl17n97g3mp5">
                    <TTIco data-id="element-2145" data-mp="1hkmf680ol3dh" />
                    <span data-id="element-2146" data-mp="1hjb5jrkk11eg">2.2M</span>
                    <IGIco data-id="element-2147" data-mp="1mjz4r0ixsojr" />
                    <span data-id="element-2148" data-mp="1mk8t6joc2nnc">158K</span>
                  </div>
                  <p className="mt-1 text-[10px] text-gray-500" data-id="element-2149" data-mp="1mhmf016xt9w5">
                    Fashion & lifestyle creator · Los Angeles
                  </p>
                </div>
              </div>

              <div className="rounded-lg border border-zano-pink/25 bg-zano-blush/60 px-3 py-2 sm:min-w-[176px]" data-id="element-2150" data-mp="2y18j58n7kqe7">
                <div className="flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-wider text-zano-pink" data-id="element-2151" data-mp="23bxawdbetmqw">
                  <Building2 size={11} data-id="element-2152" data-mp="2i9pp9258053i" /> Represented by
                </div>
                <p className="mt-1 text-[11px] font-bold text-gray-900" data-id="element-2153" data-mp="2da3c0spe6oup">
                  Clicks Talent
                </p>
                <span className="mt-1 inline-flex items-center gap-1 text-[9px] font-semibold text-emerald-700" data-id="element-2154" data-mp="1pjpisyv2mjyj">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" data-id="element-2155" data-mp="1kmgxipemlzt2" />{' '}
                  Exclusive representation
                </span>
              </div>
            </div>
          </div>

          <div className="flex h-9 items-center gap-5 border-b border-gray-100 bg-gray-50/70 px-4 text-[10px] font-semibold uppercase tracking-wide" data-id="element-2156" data-mp="1uik9emza5ct7">
            <span className="flex h-full items-center border-b-2 border-zano-pink text-zano-pink" data-id="element-2157" data-mp="16roevi3fw3i9">
              Social media
            </span>
            <span className="text-gray-400" data-id="element-2158" data-mp="3dh8frjzzw3dd">Details</span>
            <span className="text-gray-400" data-id="element-2159" data-mp="3i8zyx9gtn6th">Notes</span>
          </div>

          <div className="grid gap-3 p-3 sm:grid-cols-[1.1fr_0.9fr]" data-id="element-2160" data-mp="3iab1x72g0ajj">
            <div className="min-w-0" data-id="element-2161" data-mp="33l57dwejv8hz">
              <p className="mb-2 text-[9px] font-bold uppercase tracking-wider text-gray-400" data-id="element-2162" data-mp="33i0vecuuf8gr">
                Content style
              </p>
              <div className="flex flex-wrap gap-1.5" data-id="element-2163" data-mp="33p0tbgifdk">
                {['TikTok Shop', 'GRWM', 'Try-on hauls'].map(tag => <span key={tag} className="rounded-md border border-blue-100 bg-blue-50 px-2 py-1 text-[9px] font-semibold text-blue-600" data-id="element-2164" data-mp="51y87wdc65y7">
                    {tag}
                  </span>)}
              </div>

              <div className="mt-3 border-t border-gray-100 pt-3" data-id="element-2165" data-mp="2bs4qmdulc4uf">
                <div className="mb-2 flex items-center justify-between" data-id="element-2166" data-mp="2fv111h9z8una">
                  <span className="flex items-center gap-1.5 text-[10px] font-bold text-gray-900" data-id="element-2167" data-mp="2fs6jbxiclnxk">
                    <TTIco data-id="element-2168" data-mp="2w3gvzaoa6r1v" /> TikTok
                  </span>
                  <span className="text-[9px] text-gray-500" data-id="element-2169" data-mp="2r9cqkvunyr2w">
                    @michellethereal
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-1.5" data-id="element-2170" data-mp="2hcrn4eyalo16">
                  {[['Followers', '2.2M'], ['Avg. views', '50K'], ['Engagement', '5.3%']].map(([label, value]) => <div key={label} className="rounded-md border border-gray-100 bg-gray-50 px-2 py-2" data-id="element-2171" data-mp="3a1yhd1qprrgm">
                      <p className="text-[8px] font-medium text-gray-500" data-id="element-2172" data-mp="311ik4a0kdgk7">
                        {label}
                      </p>
                      <p className="mt-0.5 text-[11px] font-bold text-gray-900" data-id="element-2173" data-mp="1toi39ikn3aco">
                        {value}
                      </p>
                    </div>)}
                </div>
              </div>

              <div className="mt-3 border-t border-gray-100 pt-3" data-id="element-2174" data-mp="51q5s6bjzz7l">
                <p className="mb-2 text-[9px] font-bold uppercase tracking-wider text-gray-400" data-id="element-2175" data-mp="95nzu2m0ujts">
                  Audience snapshot
                </p>
                <div className="flex items-end gap-2" data-id="element-2176" data-mp="ivhdd95q590o">
                  {[34, 72, 48, 24, 18].map((height, index) => <div key={height} className="flex flex-1 flex-col items-center gap-1" data-id="element-2177" data-mp="nszewehewlyn" data-mpi={index}>
                      <div className="w-full max-w-[14px] rounded-t-sm bg-teal-500/75" style={{
                    height
                  }} data-id="element-2178" data-mp="wupfoax6ox93" />
                      <span className="text-[7px] text-gray-400" data-id="element-2179" data-mp="2ymvhzac80iam">
                        {['18–24', '25–34', '35–44', '45–54', '55+'][index]}
                      </span>
                    </div>)}
                </div>
              </div>
            </div>

            <aside className="rounded-xl border border-gray-200 bg-gray-50/70 p-3" aria-label="Talent agency contact information" data-id="element-2180" data-mp="5rllv7gutfwb">
              <div className="flex items-start justify-between gap-2" data-id="element-2181" data-mp="jpa2g33w20vk">
                <div data-id="element-2182" data-mp="erif7me6v2xp">
                  <p className="text-[9px] font-bold uppercase tracking-wider text-gray-400" data-id="element-2183" data-mp="eueb756h7hji">
                    Talent agency
                  </p>
                  <p className="mt-1 text-[11px] font-bold text-gray-900" data-id="element-2184" data-mp="or985jn3p9vo">
                    Clicks Talent
                  </p>
                </div>
                <BadgeCheck size={15} className="text-emerald-600" aria-label="Exclusive representation" data-id="element-2185" data-mp="12jytrmypwcs9" />
              </div>
              <div className="mt-3 border-t border-gray-200 pt-3" data-id="element-2186" data-mp="1cdxtygf8dmhk">
                <p className="text-[9px] font-bold uppercase tracking-wider text-gray-400" data-id="element-2187" data-mp="17ia5624nuvcl">
                  Talent manager
                </p>
                <p className="mt-1 text-[11px] font-semibold text-gray-900" data-id="element-2188" data-mp="1r0wlsci02iwt">
                  John Marcus
                </p>
                <p className="mt-0.5 text-[9px] text-gray-500" data-id="element-2189" data-mp="1h52zi1ehbb1u">
                  Partnerships lead
                </p>
              </div>
              <div className="mt-3 rounded-md border border-zano-pink/20 bg-white px-2 py-2" data-id="element-2190" data-mp="1m235ydf8qfth">
                <div className="flex min-w-0 items-center gap-1.5" data-id="element-2191" data-mp="18aol011rw4js">
                  <Mail size={11} className="shrink-0 text-zano-pink" data-id="element-2192" data-mp="1d37y6wwjsxo2" />
                  <span className="truncate text-[9px] font-semibold text-gray-700" data-id="element-2193" data-mp="ye3gdgto0vgh">
                    john@clickstalent.com
                  </span>
                  <Copy size={10} className="ml-auto shrink-0 text-gray-400" aria-hidden="true" data-id="element-2194" data-mp="13dy6vj9j3j10" />
                </div>
              </div>
              <p className="mt-2 text-[8px] leading-relaxed text-gray-500" data-id="element-2195" data-mp="te7a1axhwrxr">
                Brands contact this manager directly for bookings and rates.
              </p>
            </aside>
          </div>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 14
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.45,
        delay: 0.25
      }} className="absolute -left-3 -top-3 z-20 hidden md:block" data-id="element-2196" data-mp="14tkckzirdkxq">
          <motion.div {...floatY(0.2, 5)} className="w-48 rounded-xl border border-gray-200 bg-white p-3 shadow-[0_16px_34px_-14px_rgba(0,0,0,0.18)]" data-id="element-2197" data-mp="m32pbagp3hjr">
            <div className="flex items-center gap-2" data-id="element-2198" data-mp="c4mw91y3xy7m">
              <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-zano-blush" data-id="element-2199" data-mp="76lauad7lmsz">
                <Building2 size={13} className="text-zano-pink" data-id="element-2200" data-mp="3744lepyklmr" />
              </span>
              <div data-id="element-2201" data-mp="2eu69qtpl6cjj">
                <p className="text-[10px] font-bold text-gray-900" data-id="element-2202" data-mp="2esmrwyoa7tfn">
                  Agency-owned profile
                </p>
                <p className="text-[9px] text-gray-500" data-id="element-2203" data-mp="2ix2xszxwb0uv">
                  Representation stays visible
                </p>
              </div>
            </div>
          </motion.div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.45,
        delay: 0.45
      }} className="absolute -bottom-5 -right-3 z-20 hidden md:block" data-id="element-2204" data-mp="2sr395jjrt2c1">
          <motion.div {...floatY(0.9, 6)} className="w-56 rounded-xl border border-zano-pink/25 bg-white p-3 shadow-[0_18px_38px_-14px_rgba(0,0,0,0.2)]" data-id="element-2205" data-mp="29w4smebq0nap">
            <p className="mb-1 flex items-center gap-1.5 text-[10px] font-bold text-gray-900" data-id="element-2206" data-mp="1hbxsfbmt139f">
              <Mail size={11} className="text-zano-pink" data-id="element-2207" data-mp="r20mwr2dk9vp" /> Direct manager
              contact
            </p>
            <p className="text-[9px] leading-relaxed text-gray-500" data-id="element-2208" data-mp="w0u2qq54wcz1">
              Every profile routes brands to the agency team—not the creator.
            </p>
          </motion.div>
        </motion.div>
      </div>

      <div className="mt-3 grid gap-2 md:hidden" data-id="element-2209" data-mp="8dvntwwnxd38">
        <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white p-3 shadow-sm" data-id="element-2210" data-mp="8c12wile5kz5">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-zano-blush" data-id="element-2211" data-mp="d5n3vdi700f4">
            <Building2 size={13} className="text-zano-pink" data-id="element-2212" data-mp="3umn2g5utmurj" />
          </span>
          <p className="text-[10px] font-semibold text-gray-700" data-id="element-2213" data-mp="3fanzylbovfb">
            Agency-owned profile: representation stays visible.
          </p>
        </div>
        <div className="flex items-center gap-2 rounded-lg border border-zano-pink/25 bg-white p-3 shadow-sm" data-id="element-2214" data-mp="3qiyw1qhxe57v">
          <Mail size={13} className="shrink-0 text-zano-pink" data-id="element-2215" data-mp="36wx4mphy3us1" />
          <p className="text-[10px] font-semibold text-gray-700" data-id="element-2216" data-mp="36z9stkear1dj">
            Direct manager contact is attached to every creator.
          </p>
        </div>
      </div>
    </div>;
};
export const LayeredDiscoverabilityMockup = () => {
  return <div className="relative h-[440px] w-full" data-id="element-2217" data-mp="3bvgpemksz0it">
      <div className="absolute -inset-6 bg-gradient-to-tr from-amber-100/40 via-zano-pink/20 to-zano-lavender/30 rounded-[2.5rem] blur-2xl -z-10" data-id="element-2218" data-mp="3byw61tu4khmc" />
      <div className="absolute inset-0 bg-white rounded-2xl border border-gray-200 shadow-float overflow-hidden flex flex-col text-left" data-id="element-2219" data-mp="3kw6hpgauu8rs">
        <WindowBar title="Brand View: Roster Database" data-id="element-2220" data-mp="3isxzmz3oqxh" />

        <div className="p-4 bg-gray-50/50 border-b border-gray-100" data-id="element-2221" data-mp="3umrfx2mm9ba6">
          <div className="relative flex-1" data-id="element-2222" data-mp="ch4xkehy2z22">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zano-pink" data-id="element-2223" data-mp="ci6a6nelztlw" />
            <div className="w-full pl-9 pr-3 py-2 border-2 border-zano-pink/40 rounded-lg text-xs font-medium text-gray-900 bg-white shadow-sm" data-id="element-2224" data-mp="7ggwf00051u4">
              fashion
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-1.5 mt-2.5" data-id="element-2225" data-mp="3670b1rjt9a8a">
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium bg-white border border-gray-200 text-gray-700" data-id="element-2226" data-mp="3a9ohxtoys72m">
              <Globe size={10} className="text-gray-400" data-id="element-2227" data-mp="3k1asroxe444u" /> Location:
              <span className="font-semibold text-gray-900" data-id="element-2228" data-mp="3k35f97ob84b1">United States</span>
            </span>
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium bg-white border border-gray-200 text-gray-700" data-id="element-2229" data-mp="3p1p0t6yzre2v">
              Platform: <TTIco data-id="element-2230" data-mp="1umn9xyfheyn" />
              <span className="font-semibold text-gray-900" data-id="element-2231" data-mp="1t204teg0yat">TikTok</span>
            </span>
          </div>
        </div>

        <div className="p-4 space-y-3" data-id="element-2232" data-mp="23mj4no7a1j1c">
          <div className="border border-zano-pink/30 bg-zano-blush/20 rounded-xl p-3 shadow-sm" data-id="element-2233" data-mp="23nasudyjoehl">
            <div className="flex items-center gap-3 mb-3" data-id="element-2234" data-mp="1w3s3lpypjp0k">
              <img src="https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=60&h=60" className="w-12 h-12 rounded-full object-cover" alt="" data-id="element-2235" data-mp="1rcjterpjzzmc" />
              <div data-id="element-2236" data-mp="1icn2ejfoyoo4">
                <h4 className="text-sm font-bold text-gray-900" data-id="element-2237" data-mp="1idg9xmmh5i7b">
                  Elena Rodriguez
                </h4>
                <p className="text-[10px] text-gray-500" data-id="element-2238" data-mp="2ezi7jiq7avh3">Los Angeles, CA</p>
              </div>
            </div>
            <div className="flex items-center justify-between bg-white border border-gray-200 rounded-md px-2.5 py-2" data-id="element-2239" data-mp="261660lnsqd9v">
              <div className="flex items-center gap-2" data-id="element-2240" data-mp="212mm0qsrjbk5">
                <div className="w-6 h-6 bg-gray-900 text-white rounded flex items-center justify-center text-[8px] font-bold" data-id="element-2241" data-mp="2147g6fb60shh">
                  ABC
                </div>
                <span className="text-[11px] font-bold text-gray-900" data-id="element-2242" data-mp="pm4zpjivqpb8">
                  ABC Talent Agency
                </span>
              </div>
              <button className="bg-zano-pink text-white px-3 py-1 rounded text-[10px] font-bold" data-id="element-2243" data-mp="2iacf9mkskf6q">
                Contact Manager
              </button>
            </div>
          </div>
        </div>
      </div>

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.5,
      delay: 0.4
    }} className="absolute -top-4 -right-3 sm:-right-5 z-20" data-id="element-2244" data-mp="28k9lvl9rcz7v">
        <motion.div {...floatY(0.3, 6)} className="flex items-center gap-2 bg-white rounded-xl border border-gray-200 shadow-[0_15px_35px_-12px_rgba(0,0,0,0.15)] p-3 w-48" data-id="element-2245" data-mp="2nbjmnkj0vccv">
          <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center shrink-0" data-id="element-2246" data-mp="1bwd62im5a7xp">
            <Users size={14} className="text-blue-600" data-id="element-2247" data-mp="1grad5jzv677y" />
          </div>
          <div data-id="element-2248" data-mp="18arrlsfc020j">
            <p className="text-[11px] font-bold text-gray-900" data-id="element-2249" data-mp="18efngy8910if">
              Get Discovered
            </p>
            <p className="text-[9px] text-gray-500" data-id="element-2250" data-mp="14d3uciq61x5h">
              Brands search the database daily.
            </p>
          </div>
        </motion.div>
      </motion.div>

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.5,
      delay: 0.6
    }} className="absolute bottom-6 -left-4 sm:-left-6 z-20" data-id="element-2251" data-mp="1dbz1bx11bb9u">
        <motion.div {...floatY(1.1)} className="bg-white rounded-xl border border-gray-200 shadow-[0_20px_40px_-15px_rgba(0,0,0,0.15)] p-3 w-56" data-id="element-2252" data-mp="ukfxxp33lv7a">
          <div className="flex items-center gap-2 mb-2" data-id="element-2253" data-mp="1waed5ns6c91">
            <div className="p-1.5 bg-zano-blush rounded-md" data-id="element-2254" data-mp="1vr5sfk0t07o">
              <Mail size={12} className="text-zano-pink" data-id="element-2255" data-mp="21z1v4jv08be5" />
            </div>
            <p className="text-[11px] font-bold text-gray-900" data-id="element-2256" data-mp="26xmvcja9av2l">
              New Inbound Inquiry
            </p>
          </div>
          <div className="bg-gray-50 border border-gray-100 rounded p-2" data-id="element-2257" data-mp="2bo36txuio6mb">
            <p className="text-[10px] text-gray-600 line-clamp-2" data-id="element-2258" data-mp="2fpx2tstromg5">
              "Hi, we saw Elena in the Zano database and would love to book her
              for our upcoming campaign..."
            </p>
          </div>
        </motion.div>
      </motion.div>
    </div>;
};